import { QueryClient } from '@tanstack/react-query';
import { CacheSegment } from './types';

/**
 * Controls the caching of data across the application
 */
export class CacheController {
  private static instance: CacheController;
  private queryClient: QueryClient | null = null;
  private segments: Record<string, CacheSegment> = {
    'user': { name: 'user', dependencies: ['auth'] },
    'auth': { name: 'auth' },
    'content': { name: 'content', dependencies: ['user'] },
    'settings': { name: 'settings', dependencies: ['user'] },
    'preferences': { name: 'preferences' }
  };

  private routeSegments: Record<string, string[]> = {
    '/dashboard': ['user', 'content'],
    '/dashboard/users': ['user'],
    '/dashboard/blog': ['content'],
    '/blog': ['content'],
    '/': ['content']
  };

  private constructor() {}

  public static getInstance(): CacheController {
    if (!CacheController.instance) {
      CacheController.instance = new CacheController();
    }
    return CacheController.instance;
  }

  /**
   * Set the React Query client
   */
  public setQueryClient(client: QueryClient): void {
    this.queryClient = client;
  }

  /**
   * Prefetch data for a route
   */
  public prefetchRouteData(path: string): void {
    if (!this.queryClient) return;

    // First ensure the path is in the route segments
    if (!this.routeSegments[path]) {
      // Try to find a matching route pattern
      const matchingRoute = Object.keys(this.routeSegments).find(route => {
        if (route === path) return true;
        if (path.startsWith(route + '/')) return true;
        return false;
      });

      if (matchingRoute) {
        this.routeSegments[path] = [...this.routeSegments[matchingRoute]];
      } else {
        // Default to content segment if no match
        this.routeSegments[path] = ['content'];
      }
    }

    // Prefetch data for segments associated with this route
    const segments = this.routeSegments[path] || ['content'];
    segments.forEach(segmentName => {
      this.prefetchSegment(segmentName);
    });

    // Also prefetch the page data itself
    this.queryClient.prefetchQuery({
      queryKey: ['page', path],
      queryFn: async () => {
        // In a real implementation, you would fetch data from an API here
        return {
          id: path,
          path,
          data: { title: `Page data for ${path}` },
          timestamp: Date.now()
        };
      }
    });
  }

  /**
   * Prefetch data for a segment
   */
  public prefetchSegment(segmentName: string): void {
    if (!this.queryClient) return;
    if (!this.segments[segmentName]) return;

    // Prefetch data for the segment
    this.queryClient.prefetchQuery({
      queryKey: ['segment', segmentName],
      queryFn: async () => {
        // In a real implementation, you would fetch data from an API here
        return {
          name: segmentName,
          data: { title: `Segment data for ${segmentName}` },
          timestamp: Date.now()
        };
      }
    });

    // Also prefetch dependencies
    const dependencies = this.segments[segmentName].dependencies || [];
    dependencies.forEach(dep => {
      this.prefetchSegment(dep);
    });
  }

  /**
   * Invalidate a segment and its dependencies
   */
  public invalidateSegment(segmentName: string): void {
    if (!this.queryClient) return;
    if (!this.segments[segmentName]) return;

    // Invalidate this segment
    this.queryClient.invalidateQueries({ queryKey: ['segment', segmentName] });

    // Find segments that depend on this one
    const dependents = Object.entries(this.segments)
      .filter(([_, segment]) => segment.dependencies?.includes(segmentName))
      .map(([name, _]) => name);

    // Invalidate dependent segments recursively
    dependents.forEach(dep => {
      this.invalidateSegment(dep);
    });

    // Find routes that use this segment and invalidate page data
    const affectedRoutes = Object.entries(this.routeSegments)
      .filter(([_, segments]) => segments.includes(segmentName))
      .map(([route, _]) => route);

    affectedRoutes.forEach(route => {
      this.queryClient.invalidateQueries({ queryKey: ['page', route] });
    });
  }

  /**
   * Clear all cache
   */
  public clearCache(): void {
    if (!this.queryClient) return;
    this.queryClient.clear();
    
    // Log cache clear operation
    console.log('Cache fully cleared by CacheController');
  }

  /**
   * Clear user-specific cache segments with more detailed logging
   */
  public clearUserSpecificCache(): void {
    if (!this.queryClient) return;
    
    console.log('Clearing user-specific cache segments...');
    
    // Create a list to track invalidated segments
    const invalidatedSegments = [];
    
    // Invalidate user-specific segments
    this.invalidateSegment('user');
    invalidatedSegments.push('user');
    
    this.invalidateSegment('auth');
    invalidatedSegments.push('auth');
    
    // Also invalidate any dependent segments
    this.invalidateSegment('content');
    invalidatedSegments.push('content');
    
    this.invalidateSegment('settings');
    invalidatedSegments.push('settings');
    
    console.log(`Cleared ${invalidatedSegments.length} user-specific cache segments: ${invalidatedSegments.join(', ')}`);
    
    // Also invalidate page caches that might contain user-specific data
    // Find routes that depend on user data
    const userRelatedRoutes = Object.entries(this.routeSegments)
      .filter(([_, segments]) => segments.includes('user') || segments.includes('auth'))
      .map(([route, _]) => route);
    
    // Invalidate each route's page data
    userRelatedRoutes.forEach(route => {
      this.queryClient?.invalidateQueries({ queryKey: ['page', route] });
    });
    
    console.log(`Invalidated ${userRelatedRoutes.length} user-related page caches`);
  }
}
